/**
 * @description
 * Sync options configuration panel for Platform Sync dialog.
 * Allows users to configure sync behavior for clients and users.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 12, 2025
 * @see admin/src/frontend/components/PlatformSync/PlatformSyncDialog.tsx
 */

import React from 'react';
import { Database, Users, RefreshCw, Plus } from 'lucide-react';
import type { SyncOptions } from './types';

interface SyncOptionsPanelProps {
  options: SyncOptions;
  onChange: (options: SyncOptions) => void;
}

/**
 * Component for configuring sync options
 */
export const SyncOptionsPanel: React.FC<SyncOptionsPanelProps> = ({
  options,
  onChange,
}) => {
  const updateOption = (key: keyof SyncOptions, value: boolean) => {
    onChange({ ...options, [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Client Sync Options */}
      <div className="rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <RefreshCw className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Client Sync Options</h3>
        </div>
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.createNew}
              onChange={(e) => updateOption('createNew', e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-input"
            />
            <div className="flex-1">
              <div className="font-medium text-foreground">Create New Clients</div>
              <div className="text-sm text-muted-foreground">
                Create admin clients for platform clients that don't exist yet
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.updateExisting}
              onChange={(e) => updateOption('updateExisting', e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-input"
            />
            <div className="flex-1">
              <div className="font-medium text-foreground">Update Existing Clients</div>
              <div className="text-sm text-muted-foreground">
                Update information for clients that are already synced
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.provisionDatabase}
              onChange={(e) => updateOption('provisionDatabase', e.target.checked)}
              disabled={!options.createNew}
              className="mt-1 h-4 w-4 rounded border-input disabled:opacity-50"
            />
            <div className="flex-1">
              <div className="font-medium text-foreground flex items-center gap-2">
                <Database className="h-4 w-4" />
                Provision D1 Databases
              </div>
              <div className="text-sm text-muted-foreground">
                Automatically create and configure D1 databases for new clients
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* User Sync Options */}
      <div className="rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">User Sync Options</h3>
        </div>
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.syncUsers}
              onChange={(e) => updateOption('syncUsers', e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-input"
            />
            <div className="flex-1">
              <div className="font-medium text-foreground">Sync Users</div>
              <div className="text-sm text-muted-foreground">
                Sync users from platform for each client
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.addNewUsers}
              onChange={(e) => updateOption('addNewUsers', e.target.checked)}
              disabled={!options.syncUsers}
              className="mt-1 h-4 w-4 rounded border-input disabled:opacity-50"
            />
            <div className="flex-1">
              <div className="font-medium text-foreground flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add New Users
              </div>
              <div className="text-sm text-muted-foreground">
                Add users from platform that don't exist in admin
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.updateExistingUsers}
              onChange={(e) => updateOption('updateExistingUsers', e.target.checked)}
              disabled={!options.syncUsers}
              className="mt-1 h-4 w-4 rounded border-input disabled:opacity-50"
            />
            <div className="flex-1">
              <div className="font-medium text-foreground">Update Existing Users</div>
              <div className="text-sm text-muted-foreground">
                Update information for users that are already synced
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Warning */}
      <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
        <p className="text-sm text-orange-800">
          <strong>Note:</strong> This operation may take several minutes depending on the
          number of clients and users being synced.
        </p>
      </div>
    </div>
  );
};

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */